import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KYC } from "../../profile/useProfileApi";
import {
  IconAvatar,
  IconCheck,
  IconChart,
  IconChevronDown,
  IconClock,
  IconCopy,
  IconList,
  IconLogout,
  IconMinus,
  IconUser,
  IconVerify,
  IconWallet,
  IconX,
} from "../../profile/icons";
import "./Profile.css";

const norm = (p) => p.replace(/\/+$/, "") || "/";

// Пути, по которым подсвечиваются пункты и раскрываются группы
const ROUTES = {
  me: ["/account/me"],
  spot: ["/market"],
  portfolio: ["/account/portfolio"],
  overview: ["/account"],
  deposit: ["/account/deposit-crypto", "/account/deposit"],
  withdraw: ["/account/withdraw-crypto", "/account/withdraw"],
  convert: ["/convert"],
  wallet: ["/account/wallet"],
  kyc: ["/account/kyc"],
  history: ["/transactions"],
};

// email в панели скрываем частично (как и раньше)
const obfuscateEmail = (email) => {
  if (!email || !email.includes("@")) return email || "";
  const [local, domain] = email.split("@");
  const dot = domain ? domain.indexOf(".") : -1;
  if (dot < 1) return email;
  const name = domain.slice(0, dot);
  const ext = domain.slice(dot + 1);
  const l = local.length > 3 ? `${local.slice(0, 3)}***` : `${local[0]}***`;
  const d = name.length > 2 ? `${name.slice(0, 2)}**` : `${name[0]}**`;
  return `${l}@${d}.${ext}`;
};

const Profile = ({ open, onClose, openSpecificMenu, me, meLoading, meError, kycStatus }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const path = norm(pathname);
  const is = (key) => ROUTES[key].includes(path);

  const [tradeOpen, setTradeOpen] = useState(false);
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [copied, setCopied] = useState(null); // null | "ok" | "err"
  const closeRef = useRef(null);
  const timer = useRef();

  // при открытии раскрываем группу, в которой находится пользователь
  useEffect(() => {
    if (!open) return;
    if (is("spot") || is("portfolio")) setTradeOpen(true);
    if (is("overview") || is("deposit") || is("withdraw") || is("convert") || is("wallet")) setAssetsOpen(true);
    closeRef.current?.focus();
  }, [open]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const copyId = async () => {
    if (!me?.id) return;
    try {
      await navigator.clipboard.writeText(String(me.id));
      setCopied("ok");
    } catch (e) {
      setCopied("err");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(null), 1800);
  };

  // если уже на этой странице — просто закрываем панель
  const linkProps = (to, key) => ({
    to,
    className: is(key) ? "fxh-row on" : "fxh-row",
    "aria-current": is(key) ? "page" : undefined,
    onClick: (e) => {
      if (is(key)) {
        e.preventDefault();
        onClose();
      }
    },
  });

  const chip = {
    [KYC.VERIFIED]: ["ok", <IconCheck />, t("profile.kycChip.verified", "Пройдена")],
    [KYC.PENDING]: ["pending", <IconClock />, t("profile.kycChip.pending", "На проверке")],
    [KYC.REJECTED]: ["bad", <IconX />, t("profile.kycChip.rejected", "Отклонена")],
    [KYC.NONE]: ["idle", <IconMinus />, t("profile.kycChip.none", "Не пройдена")],
  }[kycStatus];
  const chipEl = chip ? (
    <span className={`fxh-chip ${chip[0]}`}>
      {chip[1]}
      {chip[2]}
    </span>
  ) : null;

  const identity = meLoading ? (
    <span className="fxh-muted">{t("profile.loading", "Загрузка…")}</span>
  ) : meError ? (
    <span className="fxh-danger">{t("profile.loadError", "Ошибка загрузки")}</span>
  ) : (
    obfuscateEmail(me?.email) || "—"
  );

  return (
    <aside
      className={`fxh-drawer${open ? " open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={t("profile.menu")}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="fxh-dhead">
        <h2>{t("profile.menu")}</h2>
        <button type="button" ref={closeRef} className="fxh-icon-btn" onClick={onClose} aria-label={t("common.close", "Закрыть")}>
          <IconX />
        </button>
      </div>

      <div className="fxh-scroll">
        {/* пользователь */}
        <div className="fxh-user">
          <span className="fxh-user-avatar"><IconAvatar /></span>
          <div className="fxh-user-info">
            <b>{identity}</b>
            {!meLoading && !meError && me?.id ? (
              <button
                type="button"
                className={`fxh-id mono${copied ? ` ${copied}` : ""}`}
                onClick={copyId}
                title={t("profile.idCopy")}
              >
                <span>
                  {copied === "ok"
                    ? t("profile.idCopy")
                    : copied === "err"
                    ? t("depositCrypto.errorCopyAddress")
                    : `ID ${me.id}`}
                </span>
                {copied === "ok" ? <IconCheck /> : <IconCopy />}
              </button>
            ) : null}
          </div>
        </div>

        {/* комиссии */}
        <ul className="fxh-fees">
          <li>
            <div>
              <span>{t("profile.crypto_market_spot")}</span>
              <small>{t("profile.crypto_market_spot_rates")}</small>
            </div>
            <b className="mono">0.1% / 0.1%</b>
          </li>
          <li>
            <div>
              <span>{t("profile.crypto_market_futures")}</span>
              <small>{t("profile.crypto_market_futures_rates")}</small>
            </div>
            <b className="mono">0.02% / 0.06%</b>
          </li>
        </ul>

        {/* навигация */}
        <nav className="fxh-nav" aria-label={t("profile.menu")}>
          <Link {...linkProps("/account/me", "me")}>
            <IconUser />
            <span>{t("profile.user_data")}</span>
          </Link>

          <div className="fxh-group">
            <button
              type="button"
              className={`fxh-row head${tradeOpen ? " open" : ""}`}
              aria-expanded={tradeOpen}
              onClick={() => setTradeOpen((v) => !v)}
            >
              <IconChart />
              <span>{t("profile.trade")}</span>
              <IconChevronDown className="chev" />
            </button>
            <div className={`fxh-sub${tradeOpen ? " open" : ""}`}>
              <ul>
                <li><Link {...linkProps("/market", "spot")}>{t("profile.spot_trading")}</Link></li>
                <li><Link {...linkProps("/account/portfolio", "portfolio")}>{t("profile.portfolio")}</Link></li>
              </ul>
            </div>
          </div>

          <div className="fxh-group">
            <button
              type="button"
              className={`fxh-row head${assetsOpen ? " open" : ""}`}
              aria-expanded={assetsOpen}
              onClick={() => setAssetsOpen((v) => !v)}
            >
              <IconWallet />
              <span>{t("profile.my_assets")}</span>
              <IconChevronDown className="chev" />
            </button>
            <div className={`fxh-sub${assetsOpen ? " open" : ""}`}>
              <ul>
                <li><Link {...linkProps("/account/", "overview")}>{t("profile.overview")}</Link></li>
                <li>
                  <button type="button" className={is("deposit") ? "fxh-row on" : "fxh-row"} onClick={() => openSpecificMenu("deposit")}>
                    {t("profile.replenish")}
                  </button>
                </li>
                <li>
                  <button type="button" className={is("withdraw") ? "fxh-row on" : "fxh-row"} onClick={() => openSpecificMenu("withdrawal")}>
                    {t("profile.output")}
                  </button>
                </li>
                <li><Link {...linkProps("/convert", "convert")}>{t("profile.convert")}</Link></li>
                <li><Link {...linkProps("/account/wallet", "wallet")}>{t("profile.wallet_transfer")}</Link></li>
              </ul>
            </div>
          </div>

          {kycStatus === KYC.VERIFIED ? (
            // пройденная верификация — просто индикатор, как и раньше (клик ничего не делал)
            <div className="fxh-row static">
              <IconVerify />
              <span>{t("profile.kyc_verification")}</span>
              {chipEl}
            </div>
          ) : (
            <Link {...linkProps("/account/kyc", "kyc")}>
              <IconVerify />
              <span>{t("profile.kyc_verification")}</span>
              {chipEl}
            </Link>
          )}

          <Link {...linkProps("/transactions", "history")}>
            <IconList />
            <span>{t("profile.transaction_history")}</span>
          </Link>
        </nav>
      </div>

      <div className="fxh-dfoot">
        <button
          type="button"
          className="fxh-logout"
          onClick={() => {
            localStorage.clear();
            document.location.reload();
          }}
        >
          <IconLogout />
          {t("profile.logout")}
        </button>
      </div>
    </aside>
  );
};

export default Profile;
